import React, {MouseEvent, ReactNode} from 'react';
import {observer} from 'mobx-react';
import {stylesheet} from 'typestyle';
import {observable, transaction} from 'mobx';
import {Channel} from '../../types/Channel';
import {I18n, TProvider} from '../../i18n/i18n';
import Foldout from './Foldout';
import {CompareData, ImportingAbTest} from '../../stores/CompareStore';
import {AbtestsForChannel} from '../../stores/ChannelStore';
import AbtestFoldout from './AbtestFoldout';
import {GreyBlueBackgroundColor} from '../../styles/colors';
import moment from 'moment';

interface DataSourceSelectorFoldoutProps {
    dropdown: boolean;
    disableComparison: boolean;
    closeTableDataSourceSelector: () => void;
    availableChannels: Channel[];
    selectedChannel?: Channel;
    changeSelectedChannel: (channelNames: string) => void;
    changeComparedChannel: (channelNames: string[]) => void;
    compareData: CompareData;
    abTestsForChannels: AbtestsForChannel[];
    applyAbtestCompareSettings: (channelName?: string, testId?: string, variants?: string[]) => void;
    importAbTest: (channelName: string, testId: string, lastImported?: moment.Moment) => void;
    importingAbTests: ImportingAbTest[];
}

enum TableMode {
    Channel = 'channel',
    ChannelCompare = 'channelCompare',
    ChannelVariantCompare = 'abTestVariantCompare',
}

const isTableMode = (value: any): value is TableMode => {
    return Object.values(TableMode).some((tableMode) => tableMode === value);
};

@observer
class DataSourceSelectorFoldout extends React.Component<DataSourceSelectorFoldoutProps> {
    @observable private tableMode: TableMode = this.getCurrentTableMode();
    @observable private compareChannelNames: string[] = this.getComparedChannelsFromState();

    private getCurrentTableMode(): TableMode {
        const {
            disableComparison,
            compareData: {channelCompare, variantChannelCompare, compareEnabled},
        } = this.props;
        if (disableComparison) {
            return TableMode.Channel;
        }

        if (compareEnabled) {
            if (channelCompare) {
                return TableMode.ChannelCompare;
            }
            if (variantChannelCompare) {
                return TableMode.ChannelVariantCompare;
            }
        }
        return TableMode.Channel;
    }

    private getComparedChannelsFromState(): string[] {
        const {compareEnabled, channelCompare} = this.props.compareData;

        if (compareEnabled) {
            return channelCompare ? channelCompare.channelNames : [];
        }
        return this.props.selectedChannel ? [this.props.selectedChannel.name] : [];
    }

    public handleClickOutside = (_event: MouseEvent<HTMLElement>): void => {
        this.props.closeTableDataSourceSelector();
    };

    private resetChannelCompare() {
        this.compareChannelNames = this.props.selectedChannel ? [this.props.selectedChannel.name] : [];
        this.props.changeComparedChannel([]);
    }

    private onComparedChannelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let {compareChannelNames} = this;
        if (e.currentTarget.checked) {
            compareChannelNames.push(e.currentTarget.value);
        } else {
            compareChannelNames = compareChannelNames.filter((channelName) => channelName !== e.currentTarget.value);
        }
        this.compareChannelNames = compareChannelNames;
        transaction(() => {
            this.props.changeComparedChannel(this.compareChannelNames);
        });
    };

    private changeSelectedChannel = (channelName: string) => {
        transaction(() => {
            this.resetChannelCompare();
            this.props.changeSelectedChannel(channelName);
        });
    };

    private renderChannelSelectorBody() {
        const selectedChannel = this.props.selectedChannel ? this.props.selectedChannel.name : '';
        const {compareData, abTestsForChannels, importAbTest, importingAbTests} = this.props;
        const {variantChannelCompare, compareEnabled} = compareData;

        if (this.tableMode === TableMode.ChannelCompare) {
            return this.props.availableChannels.map((channel) => (
                <label key={channel.name}>
                    <input
                        type="checkbox"
                        onChange={this.onComparedChannelChange}
                        checked={this.compareChannelNames.some((comparedChannel) => comparedChannel === channel.name)}
                        value={channel.name}
                    />
                    {channel.name}
                </label>
            ));
        } else if (this.tableMode === TableMode.ChannelVariantCompare) {
            return (
                <AbtestFoldout
                    importAbTest={importAbTest}
                    importingAbTests={importingAbTests}
                    abTestsForChannels={abTestsForChannels}
                    variantChannelCompare={variantChannelCompare ? {...variantChannelCompare, channelName: selectedChannel} : undefined}
                    applyAbtestCompareSettings={this.props.applyAbtestCompareSettings}
                />
            );
        } else {
            const channelIsSelected = (channelName: string): boolean => !compareEnabled && channelName === selectedChannel;

            return this.props.availableChannels.map((channel) => (
                <label key={channel.name} onClick={() => this.changeSelectedChannel(channel.name)}>
                    <input type="radio" readOnly checked={channelIsSelected(channel.name)} value={channel.name} />
                    {channel.name}
                </label>
            ));
        }
    }

    private renderChannelInfo(): React.ReactNode {
        if (this.tableMode === TableMode.ChannelVariantCompare) {
            return <I18n i18nKey="abtest:chooseAbtestOrMultipleTestVariants" />;
        }
        if (this.tableMode === TableMode.ChannelCompare) {
            return <I18n i18nKey="table:chooseMultipleChannelsForComparison" />;
        }
        return <I18n i18nKey="table:chooseChannel" />;
    }

    private changeTableMode = (e: React.ChangeEvent<HTMLSelectElement>): void => {
        const selectedValue = e.currentTarget.value;
        if (isTableMode(selectedValue)) {
            this.tableMode = selectedValue;
        }
    };

    public render() {
        const {dropdown, disableComparison} = this.props;
        const title: ReactNode = <I18n i18nKey="table:dataSource" />;
        const body = (
            <React.Fragment>
                <div className={dataSourceCss.channelSelectorHeader}>
                    <div className={dataSourceCss.channelSelectContainer}>
                        <TProvider>
                            {(t) => (
                                <select value={this.tableMode} onChange={this.changeTableMode} className={dataSourceCss.channelSelect}>
                                    <option value="channel">{t('table:singleChannel')}</option>
                                    {disableComparison ? null : <option value="channelCompare">{t('table:channelComparison')}</option>}
                                    {disableComparison ? null : <option value="abTestVariantCompare">{t('table:channelVariantComparison')}</option>}
                                </select>
                            )}
                        </TProvider>
                    </div>
                    <div className={dataSourceCss.channelSelectInfo}>{this.renderChannelInfo()}</div>
                </div>
                <div className={dataSourceCss.channelSelectorBody}>{this.renderChannelSelectorBody()}</div>
            </React.Fragment>
        );

        return (
            <Foldout
                minWidth={300}
                bottomPosition={25}
                topPosition={dropdown ? 25 : undefined}
                arrowLeftPosition={70}
                title={title}
                body={body}
                onClose={this.props.closeTableDataSourceSelector}
            />
        );
    }
}

export default DataSourceSelectorFoldout;

const dataSourceCss = stylesheet({
    channelSelect: {
        height: 30,
    },
    channelSelectContainer: {
        flexGrow: 1,
    },
    closeButton: {
        width: 30,
    },
    channelSelectorHeader: {},
    channelSelectInfo: {
        marginTop: 5,
        fontSize: 12,
        fontWeight: 'bold',
    },
    channelSelectorBody: {
        overflow: 'auto',
        height: 200,
        $nest: {
            label: {
                display: 'block',
                marginTop: 5,
                marginBottom: 5,
                marginRight: 5,
                $nest: {
                    input: {
                        marginRight: 5,
                    },
                },
            },
        },
    },
    selected: {
        backgroundColor: GreyBlueBackgroundColor,
    },
});



// WEBPACK FOOTER //
// ./src/views/Table/DataSourceSelectorFoldout.tsx